import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import deepLinkHandler from '../utils/deeplink';
import { 
  BellIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  ChevronRightIcon,
  EyeIcon,
  PaperClipIcon,
  DocumentIcon,
  PhotoIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const Notice = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: noticeIdFromUrl } = useParams();
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [modalReady, setModalReady] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // 유튜브 스타일 삽입
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* 일반 유튜브 영상 (16:9) */
      .youtube-video-container {
        position: relative;
        width: 100%;
        max-width: 800px;
        margin: 2rem auto;
        padding-bottom: 56.25%; /* 16:9 비율 */
        height: 0;
        overflow: hidden;
        background: #000;
        border-radius: 0.5rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }
      
      .youtube-video-container iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
      
      /* 유튜브 쇼츠 (9:16) */
      .youtube-shorts-container {
        position: relative;
        width: 100%;
        max-width: 450px;
        margin: 2rem auto;
        padding-bottom: 177.78%; /* 9:16 비율 */
        height: 0;
        overflow: hidden;
        background: #000;
        border-radius: 0.5rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }
      
      .youtube-shorts-container iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
      
      /* 모바일에서 쇼츠 크기 조정 */
      @media (max-width: 640px) {
        .youtube-shorts-container {
          max-width: 300px;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { data: noticeData, isLoading, refetch } = useQuery(
    ['notices', page, searchQuery],
    () => api.get(`/notices?page=${page}&limit=10${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`).then(res => res.data),
    {
      enabled: !!user
    }
  );

  // URL 파라미터로 특정 공지 열기
  useEffect(() => {
    const openNoticeFromUrl = async () => {
      // URL 경로에서 공지 ID 추출 (예: /notices/123)
      const pathMatch = location.pathname.match(/\/notices\/(\d+)/);
      const noticeId = pathMatch ? pathMatch[1] : null;
      
      if (noticeId) {
        console.log('[공지사항] URL에서 공지 ID 감지:', noticeId);
        try {
          const response = await api.get(`/notices/${noticeId}`);
          console.log('[공지사항] 공지 데이터 로드 성공');
          setSelectedNotice(response.data);
          
          // 모달 준비 상태 설정
          setTimeout(() => {
            setModalReady(true);
          }, 100);
          
          // 조회수 증가
          await api.patch(`/notices/${noticeId}/view`);
          
          // URL을 /notices로 변경하되, 모달은 유지
          // replace: false로 변경하여 브라우저 히스토리 유지
          window.history.replaceState(null, '', '/notices');
        } catch (error) {
          console.error('공지사항 불러오기 실패:', error);
          // 실패 시에도 목록 페이지로 이동
          navigate('/notices');
        }
      }
    };
    
    openNoticeFromUrl();
  }, [location.pathname]);

  // 딥링크 자동 실행 제거 - 앱으로 자동 전환하지 않음

  const handleNoticeClick = async (notice) => {
    // 모달 준비 상태 초기화
    setModalReady(false);
    
    // 상세 정보 가져오기 (첨부파일 포함)
    try {
      const response = await api.get(`/notices/${notice.id}`);
      setSelectedNotice(response.data);
      
      // 조회수 증가
      await api.patch(`/notices/${notice.id}/view`);
      
      // 약간의 지연 후 모달을 렌더링 (DOM 준비 시간)
      setTimeout(() => {
        setModalReady(true);
      }, 50);
    } catch (error) {
      console.error('공지사항 상세 정보 조회 실패:', error);
      // 실패 시 기본 정보만 표시
      setSelectedNotice(notice);
      setTimeout(() => {
        setModalReady(true);
      }, 50);
    }
  };

  const formatDate = (date) => {
    return format(new Date(date), 'yyyy.MM.dd HH:mm', { locale: ko });
  };

  const getTargetAudienceLabel = (target) => {
    if (!target) return '전체';
    
    // 개별 공지인 경우
    if (target === 'individual') {
      return '개별 공지';
    }
    
    const targets = target.split(',');
    const labels = targets.map(t => {
      switch (t.trim()) {
        case 'all': return '전체';
        case 'contractor': return '소장';
        case 'customer': return '고객';
        case 'vip_customer': return 'VIP 고객';
        default: return t.trim();
      }
    });
    
    return labels.join(', ');
  };

  const getTargetAudienceColor = (target) => {
    if (!target) return 'bg-gray-100 text-gray-800';
    
    // 개별 공지인 경우 특별한 스타일 적용
    if (target === 'individual') {
      return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm';
    }
    
    const targets = target.split(',');
    
    if (targets.includes('all')) {
      return 'bg-blue-100 text-blue-800';
    } else if (targets.length > 1) {
      return 'bg-indigo-100 text-indigo-800';
    } else {
      switch (targets[0].trim()) {
        case 'contractor': return 'bg-purple-100 text-purple-800';
        case 'customer': return 'bg-green-100 text-green-800';
        case 'vip_customer': return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">공지사항을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-xl">
                <BellIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  공지사항
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  새로운 소식과 업데이트
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* 검색 버튼/입력창 */}
              <div className="flex items-center gap-2">
                {!showSearch ? (
                  <>
                    <button
                      onClick={() => setShowSearch(true)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="검색"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-600" />
                    </button>
                    <div className="px-3 py-1.5 bg-gray-100 rounded-lg">
                      <span className="text-sm font-medium text-gray-900">{noticeData?.total || 0}개</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            setPage(1);
                            refetch();
                          }
                        }}
                        placeholder="제목, 내용 검색..."
                        className="w-40 sm:w-64 px-3 py-1.5 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        autoFocus
                      />
                      {searchQuery && (
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            setPage(1);
                            refetch();
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowSearch(false);
                        setSearchQuery('');
                        setPage(1);
                        refetch();
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="닫기"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* 검색 결과 표시 */}
        {searchQuery && (
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              <span className="font-medium">"{searchQuery}"</span> 검색 결과 
              <span className="font-medium text-blue-600 ml-1">{noticeData?.total || 0}개</span>
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setPage(1);
                refetch();
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              전체 보기
            </button>
          </div>
        )}
        
        {/* 공지사항 목록 */}
        <div className="space-y-4">
          {noticeData?.notices?.map((notice) => (
            <div
              key={notice.id}
              onClick={() => handleNoticeClick(notice)}
              className="group relative bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden"
            >
              {/* 중요 표시 리본 */}
              {notice.is_important ? (
                <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden">
                  <div className="absolute transform rotate-45 bg-red-500 text-white text-xs font-bold py-1 right-[-25px] top-[15px] w-[100px] text-center shadow-sm">
                    중요
                  </div>
                </div>
              ) : null}
              
              <div className="p-4 sm:p-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  {/* 아이콘 영역 */}
                  <div className="flex-shrink-0">
                    {notice.is_important ? (
                      <div className="relative">
                        <div className="absolute inset-0 bg-red-500 rounded-lg sm:rounded-xl blur-lg opacity-30 group-hover:opacity-40 transition-opacity"></div>
                        <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
                          <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="absolute inset-0 bg-blue-500 rounded-lg sm:rounded-xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity"></div>
                        <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
                          <BellIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* 컨텐츠 영역 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-lg ${getTargetAudienceColor(notice.target_audience)}`}>
                        {notice.target_audience === 'individual' ? '🔒 ' : ''}{getTargetAudienceLabel(notice.target_audience)}
                      </span>
                    </div>
                    
                    <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-3 sm:line-clamp-2">
                      {notice.title}
                    </h3>
                    
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                        <span className="hidden sm:inline">{formatDate(notice.created_at)}</span>
                        <span className="sm:hidden">{format(new Date(notice.created_at), 'MM.dd', { locale: ko })}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <UserIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                        <span>{notice.author_name || '관리자'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <EyeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                        <span>조회 {notice.view_count || 0}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* 화살표 */}
                  <div className="hidden sm:flex flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronRightIcon className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 페이지네이션 */}
        {noticeData?.pages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-3 rounded-l-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="h-5 w-5 rotate-180" />
                <span className="ml-1">이전</span>
              </button>
              
              <div className="relative inline-flex items-center px-6 py-3 border-t border-b border-gray-300 bg-gray-50">
                <span className="text-sm text-gray-700">
                  페이지 <span className="font-bold text-blue-600">{page}</span> / <span className="font-medium">{noticeData?.pages}</span>
                </span>
              </div>
              
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === noticeData?.pages}
                className="relative inline-flex items-center px-4 py-3 rounded-r-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <span className="mr-1">다음</span>
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </nav>
          </div>
        )}

        {/* 공지사항이 없을 때 */}
        {noticeData?.notices?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="absolute inset-0 bg-gray-200 rounded-full blur-2xl opacity-30"></div>
              <div className="relative bg-white rounded-full p-8 shadow-lg">
                {searchQuery ? (
                  <MagnifyingGlassIcon className="h-16 w-16 text-gray-400" />
                ) : (
                  <BellIcon className="h-16 w-16 text-gray-400" />
                )}
              </div>
            </div>
            <h3 className="mt-6 text-lg font-semibold text-gray-900">
              {searchQuery ? '검색 결과가 없습니다' : '공지사항이 없습니다'}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery ? '다른 검색어를 입력해보세요.' : '새로운 소식이 등록되면 여기에 표시됩니다.'}
            </p>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setPage(1);
                  refetch();
                }}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                전체 공지사항 보기
              </button>
            )}
          </div>
        )}
      </div>


      {/* 공지사항 상세 모달 */}
      {selectedNotice && modalReady && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] mb-16 flex flex-col overflow-hidden" style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}>
            {/* 헤더 */}
            <div className="flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 md:px-6 py-5 border-b border-blue-100">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {selectedNotice.is_important ? (
                      <div className="bg-red-100 px-3 py-1 rounded-lg inline-flex items-center gap-1.5">
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                        <span className="text-sm font-semibold text-red-700">중요 공지</span>
                      </div>
                    ) : null}
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-lg ${getTargetAudienceColor(selectedNotice.target_audience)}`}>
                      {getTargetAudienceLabel(selectedNotice.target_audience)}
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent break-all min-w-0">
                    {selectedNotice.title}
                  </h2>
                  
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <span className="hidden sm:inline">{formatDate(selectedNotice.created_at)}</span>
                      <span className="sm:hidden">{format(new Date(selectedNotice.created_at), 'MM.dd HH:mm', { locale: ko })}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <UserIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <span>{selectedNotice.author_name || '관리자'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <EyeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <span className="font-medium">조회 {selectedNotice.view_count || 0}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedNotice(null);
                    setModalReady(false);
                  }}
                  className="text-gray-400 hover:text-gray-500 flex-shrink-0 p-2 hover:bg-white/50 rounded-full transition-all -mr-1"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* 컨텐츠 - 스크롤 영역 */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-white to-gray-50/50 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 sm:p-6 pb-20">
                <div className="max-w-none text-gray-900" style={{ wordBreak: 'break-word' }}>
                  <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    img: ({node, ...props}) => (
                      <div className="flex justify-center my-4">
                        <img 
                          {...props} 
                          className="max-w-full h-auto rounded-lg" 
                          alt="" 
                          loading="lazy"
                          style={{ display: 'block', height: 'auto' }}
                          onLoad={(e) => {
                            e.target.style.opacity = '1';
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ),
                    a: ({node, ...props}) => (
                      <a {...props} className="text-blue-600 underline break-all" target="_blank" rel="noopener noreferrer" />
                    ),
                    p: ({node, ...props}) => (
                      <p {...props} className="mb-3 whitespace-pre-wrap break-words leading-relaxed" />
                    ),
                    table: ({node, ...props}) => (
                      <div className="overflow-x-auto my-4">
                        <table {...props} className="min-w-full divide-y divide-gray-200" />
                      </div>
                    ),
                    thead: ({node, ...props}) => (
                      <thead {...props} className="bg-gray-50" />
                    ),
                    th: ({node, ...props}) => (
                      <th {...props} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    ),
                    td: ({node, ...props}) => (
                      <td {...props} className="px-6 py-4 whitespace-normal break-words text-sm text-gray-900" />
                    ),
                    blockquote: ({node, ...props}) => (
                      <blockquote {...props} className="pl-4 border-l-4 border-gray-300 text-gray-600 italic my-3" />
                    ),
                    code: ({node, inline, ...props}) => 
                      inline ? (
                        <code {...props} className="px-1 py-0.5 bg-gray-100 text-red-600 rounded text-sm break-all" />
                      ) : (
                        <code {...props} className="block p-3 bg-gray-100 text-gray-900 rounded overflow-x-auto my-3 whitespace-pre-wrap text-sm" />
                      ),
                    pre: ({node, ...props}) => (
                      <pre {...props} className="my-3 whitespace-pre-wrap" />
                    ),
                  }}
                >
                  {selectedNotice.content}
                  </ReactMarkdown>
                </div>
                
                {/* 첨부파일 섹션 */}
                {selectedNotice.attachments && selectedNotice.attachments.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <PaperClipIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                      첨부파일 ({selectedNotice.attachments.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedNotice.attachments.map((file, index) => (
                        <a
                          key={index}
                          href={file.url}
                          download={file.name}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 group"
                        >
                          <div className="flex-shrink-0">
                            {file.type?.startsWith('image/') ? (
                              <PhotoIcon className="h-5 w-5 text-blue-500" />
                            ) : (
                              <DocumentIcon className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {file.size && `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                            </p>
                          </div>
                          <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* 푸터 - 고정 */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 md:px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setSelectedNotice(null);
                  setModalReady(false);
                }}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02]"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notice;